import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { ComparisonResult, Client, DBSnapshot, NewSnapshotData, WordPressConnection } from './types';
import databaseService from './services/databaseService'; // Import default export
const { client: clientService, snapshot: snapshotService, testConnection } = databaseService; // Destructure services
import { extractPluginNamesFromText } from './services/geminiService';
import { getWordPressPlugins } from './services/wordpressService';
import axios from 'axios';
import PluginListCard from './components/PluginListCard';
// import { DatabaseTest } from './components/DatabaseTest'; // DatabaseTest can be removed or adapted later

const App: React.FC = () => {
  // Authentication state
  const [authKey] = useState<string>(import.meta.env.VITE_LOGIN_KEY!);
  console.log('Auth key from env:', import.meta.env.VITE_LOGIN_KEY ? 'Found' : 'Not found');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false); // Forcing to false for testing
  console.log('Initial isAuthenticated:', false);
  const [userInputKey, setUserInputKey] = useState<string>('');

  useEffect(() => {
    console.log('Auth useEffect running');
    // Check localStorage for a saved key
    const savedKey = localStorage.getItem('authKey');
    console.log('Saved key from localStorage:', savedKey ? 'Exists' : 'Not found');
    if (savedKey && savedKey === authKey) {
      console.log('Auth: Valid key in localStorage');
      setIsAuthenticated(true);
      return; // Already authenticated via localStorage
    } else if (savedKey) {
      console.log('Auth: Invalid key in localStorage');
    }

    // Check URL query parameters for a key
    const queryParams = new URLSearchParams(window.location.search);
    const keyFromUrl = queryParams.get('key');
    console.log('Key from URL params:', keyFromUrl ? 'Exists' : 'Not found');
    if (keyFromUrl) {
      console.log('Auth: Key from URL matches authKey?', keyFromUrl === authKey);
      if (keyFromUrl === authKey) {
        console.log('Auth: Valid key from URL');
        setIsAuthenticated(true);
        localStorage.setItem('authKey', authKey); // Save to localStorage
      }
    }
  }, [authKey]); // Depend on authKey in case it changes (e.g., fetched async)

  const handleKeyCheck = () => {
    console.log('Checking key input:', userInputKey);
    if (userInputKey === authKey) {
      console.log('Auth: Valid key entered');
      setIsAuthenticated(true);
      localStorage.setItem('authKey', authKey);
    } else {
      alert('Invalid key'); // Simple feedback for now
    }
  };

  useEffect(() => {
    const checkDbConnection = async () => {
      console.log('Attempting to test database connection from App.tsx...');
      const isConnected = await testConnection();
      if (isConnected) {
        console.log('App.tsx: testConnection reported: Successfully connected.');
      } else {
        console.error('App.tsx: testConnection reported: Failed to connect.');
      }
    };
    checkDbConnection();
  }, []);
  const [clientNameInput, setClientNameInput] = useState<string>('');
  const [inputText, setInputText] = useState<string>('');
  const [wordPressUrl, setWordPressUrl] = useState<string>('');
  const [wordPressUsername, setWordPressUsername] = useState<string>('');
  const [wordPressPassword, setWordPressPassword] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'manual' | 'wordpress'>('wordpress');
  const [snapshots, setSnapshots] = useState<DBSnapshot[]>([]);
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [currentAnalysis, setCurrentAnalysis] = useState<{ plugins: string[]; timestamp: string; clientName: string } | null>(null);
  const [comparison, setComparison] = useState<ComparisonResult | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isTestingApi, setIsTestingApi] = useState<boolean>(false);
  const [apiStatus, setApiStatus] = useState<{ status: 'idle' | 'success' | 'error'; message: string }>({ status: 'idle', message: '' });
  const [deletingSnapshotId, setDeletingSnapshotId] = useState<string | null>(null);

  // New state for the current client's WordPress connection
  const [currentClientWordPressConnection, setCurrentClientWordPressConnection] = useState<WordPressConnection | null>(null);

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      setIsLoading(true);
      try {
        const clients = await clientService.getAll();
        setAvailableClients(clients);
        if (clients.length > 0) {
          // Optionally, select the first client by default
          // setSelectedClientId(clients[0].id);
          // setClientNameInput(clients[0].name);
        }
      } catch (err) {
        setError("Failed to load clients. Please ensure the database is connected and refresh.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchClients();
  }, []);

  // Effect to load WordPress connection when selectedClientId changes
  useEffect(() => {
    if (selectedClientId) {
      const fetchConnection = async () => {
        setIsLoading(true);
        try {
          const connection = await databaseService.wordPressConnection.getByClientId(selectedClientId);
          if (connection) {
            setCurrentClientWordPressConnection(connection);
            setWordPressUrl(connection.wordpress_url);
            // Assuming api_key is username and api_secret is password for now
            setWordPressUsername(connection.api_key || '');
            setWordPressPassword(connection.api_secret || '');
            setApiStatus({ status: 'success', message: '' });
          } else {
            setCurrentClientWordPressConnection(null);
            setWordPressUsername('');
            setWordPressPassword('');
            setApiStatus({ status: 'idle', message: '' });
          }
        } catch (error) {
          console.error("Failed to load WordPress connection for client:", error);
          setError("Failed to load WordPress connection details.");
          setCurrentClientWordPressConnection(null);
          setWordPressUsername('');
          setWordPressPassword('');
        } finally {
          setIsLoading(false);
        }
      };
      fetchConnection();
    } else {
      // Clear connection details if no client is selected
      setCurrentClientWordPressConnection(null);
      setWordPressUrl(''); // Clear URL when no client selected
      setWordPressUsername('');
      setWordPressPassword('');
      setApiStatus({ status: 'idle', message: '' });
    }
  }, [selectedClientId]);

  // Fetch snapshots when selectedClientId changes
  useEffect(() => {
    if (selectedClientId) {
      const fetchSnapshotsForClient = async () => {
        setIsLoading(true);
        try {
          const clientSnapshots = await snapshotService.getByClientId(selectedClientId);
          setSnapshots(clientSnapshots);
        } catch (err) {
          setError(`Failed to load snapshots for client ${selectedClientId}.`);
          console.error(err);
        } finally {
          setIsLoading(false);
        }
      };
      fetchSnapshotsForClient();
    } else {
      setSnapshots([]); // Clear snapshots if no client is selected
    }
  }, [selectedClientId]);

  const fetchPluginsFromWordPress = async (): Promise<string[]> => {
    if (!wordPressUrl.trim()) {
      throw new Error('Please enter a WordPress site URL');
    }

    try {
      const plugins = await getWordPressPlugins(
        wordPressUrl,
        wordPressUsername || undefined,
        wordPressPassword || undefined
      );
      
      if (plugins.length === 0) {
        throw new Error('No plugins found on this WordPress site');
      }
      
      return plugins.map(p => p.name || p.plugin);
    } catch (error) {
      console.error('Error fetching plugins from WordPress:', error);
      throw new Error(`Failed to fetch plugins: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteClient = async () => {
    if (!selectedClientId) {
      setError("No client selected to delete.");
      setApiStatus({ status: 'error', message: "No client selected to delete." });
      return;
    }

    const clientToDelete = availableClients.find(c => c.id === selectedClientId);
    const clientNameToConfirm = clientToDelete ? clientToDelete.name : clientNameInput; // Use clientNameInput as fallback

    if (window.confirm(`Are you sure you want to delete the client "${clientNameToConfirm}" and ALL associated data (snapshots, WordPress connection)? This action cannot be undone.`)) {
      setError(null); // Clear previous global errors
      setApiStatus({ status: 'idle', message: `Deleting client ${clientNameToConfirm}...` });
      try {
        await databaseService.client.deleteById(selectedClientId);

        // Clear all related states
        setAvailableClients(prev => prev.filter(c => c.id !== selectedClientId));
        setSelectedClientId(null);
        setClientNameInput(''); // Clear the input field
        setSnapshots([]);
        setCurrentAnalysis(null);
        setComparison(null);
        setWordPressUrl('');
        setWordPressUsername('');
        setWordPressPassword('');
        setCurrentClientWordPressConnection(null);

        setApiStatus({ status: 'success', message: `Client "${clientNameToConfirm}" and all associated data deleted successfully.` });
      } catch (err) {
        console.error("Failed to delete client:", err);
        const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
        setError(`Failed to delete client: ${errorMessage}`); // Set global error
        setApiStatus({ status: 'error', message: `Failed to delete client: ${errorMessage}` });
      } finally {
        // Clear API status message after a delay
        setTimeout(() => {
          setApiStatus(prev => ({ ...prev, message: '' }));
        }, 7000);
      }
    }
  };

  const handleDeleteSnapshot = async (snapshotId: string, snapshotTimestamp: string) => {
    const clientDisplayName = clientNameInput || availableClients.find(c => c.id === selectedClientId)?.name || "this client";
    // Ensure getFormattedDate is available and correctly formats the timestamp for the confirmation
    const formattedTimestamp = getFormattedDate(snapshotTimestamp);

    if (window.confirm(`Are you sure you want to delete the snapshot for ${clientDisplayName} taken on ${formattedTimestamp}?`)) {
      setDeletingSnapshotId(snapshotId);
      // Use setIsLoading for global loading indicator if preferred, or rely on deletingSnapshotId for specific button state
      // setIsLoading(true);
      try {
        await databaseService.snapshot.deleteById(snapshotId);
        setSnapshots(prevSnapshots => prevSnapshots.filter(s => s.id !== snapshotId));
        setApiStatus({ status: 'success', message: `Snapshot from ${formattedTimestamp} deleted successfully.` });
        setError(null); // Clear previous global errors if any
      } catch (err) {
        console.error("Failed to delete snapshot:", err);
        const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
        // Set error for global display, and also in apiStatus for contextual display
        setError(`Failed to delete snapshot: ${errorMessage}`);
        setApiStatus({ status: 'error', message: `Failed to delete snapshot. ${errorMessage}` });
      } finally {
        setDeletingSnapshotId(null);
        // setIsLoading(false);
        // Clear API status message after a delay
        setTimeout(() => {
          setApiStatus(prev => ({ ...prev, message: '' }));
        }, 7000);
      }
    }
  };

  const handleAnalyze = useCallback(async () => {
    if (!clientNameInput.trim()) {
      setError("Please enter a client name (e.g., example.com).");
      return;
    }
    
    let pluginsToAnalyze: string[] = [];
    
    try {
      setIsLoading(true);
      setError(null);
      setCurrentAnalysis(null);
      setComparison(null);
      
      // Get plugins based on active tab
      if (activeTab === 'manual') {
        if (!inputText.trim()) {
          throw new Error("Please paste some text to analyze.");
        }
        pluginsToAnalyze = await extractPluginNamesFromText(inputText);
      } else { // WordPress tab
        pluginsToAnalyze = await fetchPluginsFromWordPress();
      }
      
      if (pluginsToAnalyze.length === 0) {
        throw new Error('No plugins found to analyze');
      }

      const trimmedClientName = clientNameInput.trim();
      let currentClient: Client | null = await clientService.findByName(trimmedClientName);
      let currentClientIdToUse: string;

      if (currentClient) {
        currentClientIdToUse = currentClient.id;
      } else {
        const newClient = await clientService.create(trimmedClientName);
        currentClient = newClient;
        currentClientIdToUse = newClient.id;
        setAvailableClients(prev => [...prev, newClient].sort((a,b) => a.name.localeCompare(b.name)));
      }
      setSelectedClientId(currentClientIdToUse); // Set selected client ID

      const analysisTimestamp = new Date(); // Use Date object here

      const newSnapshotData: NewSnapshotData = {
        client_id: currentClientIdToUse,
        timestamp: analysisTimestamp,
        plugins: pluginsToAnalyze,
        raw_input: activeTab === 'manual' ? inputText : `WordPress Site: ${wordPressUrl}`,
      };

      const createdSnapshot = await snapshotService.create(newSnapshotData);

      // After creating, snapshots will be refetched by the useEffect hook listening to selectedClientId
      // However, to make the current analysis reflect immediately, we can update it here.
       setCurrentAnalysis({
        plugins: pluginsToAnalyze,
        timestamp: createdSnapshot.timestamp, // Use timestamp from DB response
        clientName: trimmedClientName
      });


      // Fetch all snapshots for this client to perform comparison
      const clientSnapshots = await snapshotService.getByClientId(currentClientIdToUse);
      setSnapshots(clientSnapshots); // Update snapshots state immediately for comparison

      // Find the most recent *previous* snapshot for the *same client*
      // The createdSnapshot is the newest, so we filter it out for comparison
      const previousSnapshotsForClient = clientSnapshots.filter(
        s => s.id !== createdSnapshot.id
      ).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      if (previousSnapshotsForClient.length > 0) {
        const previousSnapshot = previousSnapshotsForClient[0];
        const oldPlugins = previousSnapshot.plugins; // Already string[]
        const newPlugins = pluginsToAnalyze;

        const oldSet = new Set(oldPlugins);
        const newSet = new Set(newPlugins);

        const added = newPlugins.filter(plugin => !oldSet.has(plugin));
        const removed = oldPlugins.filter(plugin => !newSet.has(plugin));
        const common = newPlugins.filter(plugin => oldSet.has(plugin));
        
        setComparison({ added, removed, common });
      } else {
        setComparison({ added: pluginsToAnalyze, removed: [], common: [] });
      }
      setInputText('');
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred during analysis.");
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [inputText, clientNameInput, clientService, snapshotService, activeTab, wordPressUrl, wordPressUsername, wordPressPassword]); // Added service dependencies

  const getFormattedDate = (isoDateString: string): string => {
    return new Date(isoDateString).toLocaleString();
  };
  
  // Handler for selecting a client from a list (if we add a dropdown/list later)
  // Function to check if a string looks like a domain
  const looksLikeDomain = (str: string): boolean => {
    // Simple regex to match domains (e.g., example.com, sub.example.com)
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
    return domainRegex.test(str);
  };

  // Handle client name input changes and auto-fill WordPress URL if applicable
  const handleClientNameChange = (value: string) => {
    setClientNameInput(value);
    
    // If WordPress URL is empty and the input looks like a domain, auto-fill it
    if (!wordPressUrl && looksLikeDomain(value)) {
      // Remove any existing protocol and trailing slashes
      const cleanedDomain = value.replace(/^https?:\/\//, '').replace(/\/+$/, '');
      setWordPressUrl(`https://${cleanedDomain}`);
    }
    
    // Clear selected client if the name doesn't match anymore
    const currentSelectedClient = availableClients.find(c => c.id === selectedClientId);
    if (currentSelectedClient && currentSelectedClient.name !== value) {
      setSelectedClientId(null); // This will trigger the useEffect to clear connection details
      setSnapshots([]);
      setCurrentAnalysis(null);
      setComparison(null);
    }
  };

  const handleClientSelection = (clientId: string) => {
    const client = availableClients.find(c => c.id === clientId);
    if (client) {
      // Clear the WordPress URL when selecting a new client
      setWordPressUrl('');
      setSelectedClientId(client.id); // This will trigger the useEffect to load connection details
      setClientNameInput(client.name);
      setCurrentAnalysis(null);
      setComparison(null);
      
      // If client name looks like a domain, auto-fill the WordPress URL
      if (looksLikeDomain(client.name)) {
        const cleanedDomain = client.name.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
        setWordPressUrl(`https://www.${cleanedDomain}`);
      }
    }
  };

  const saveClientWordPressConnection = async () => {
    const trimmedClientName = clientNameInput.trim();

    if (!trimmedClientName) {
      setError("Please enter a client name before saving a connection.");
      setApiStatus({ status: 'error', message: "Client name is required to save a connection." });
      return;
    }

    if (!wordPressUrl.trim()) {
      setError("WordPress URL cannot be empty.");
      setApiStatus({ status: 'error', message: "WordPress URL is required." });
      return;
    }

    setIsLoading(true);
    setApiStatus({ status: 'idle', message: 'Saving connection...' });

    let finalClientId = selectedClientId;

    if (!finalClientId) { // If no client is selected, or name input changed, find/create by name
      let client = await clientService.findByName(trimmedClientName);
      if (!client) {
        client = await clientService.create(trimmedClientName);
        setAvailableClients(prev => [...prev, client!].sort((a, b) => a.name.localeCompare(b.name)));
      }
      finalClientId = client.id;
      setSelectedClientId(client.id); // Ensure the state reflects the client being used
    }
    // Username (api_key) and Password (api_secret) can be optional based on schema

    let newConnectionData: WordPressConnection = {
      id: currentClientWordPressConnection?.id || '', // Will be ignored by DB on insert, or used for update context if needed
      client_id: finalClientId!, 
      wordpress_url: wordPressUrl.trim(),
      api_key: wordPressUsername.trim() || undefined, // Store empty as undefined
      api_secret: wordPressPassword.trim() || undefined, // Store empty as undefined
      is_connected: false, 
      last_tested: undefined, // Explicitly undefined for new/modified, might become null in DB
    };

    // If current connection details in state match what's being saved from form, preserve test status
    if (currentClientWordPressConnection &&
        currentClientWordPressConnection.client_id === finalClientId &&
        currentClientWordPressConnection.wordpress_url === newConnectionData.wordpress_url &&
        (currentClientWordPressConnection.api_key || '') === (newConnectionData.api_key || '') &&
        (currentClientWordPressConnection.api_secret || '') === (newConnectionData.api_secret || '')) {
      newConnectionData.is_connected = currentClientWordPressConnection.is_connected || false;
      newConnectionData.last_tested = currentClientWordPressConnection.last_tested;
    }

    try {
      const savedConnection = await databaseService.wordPressConnection.save(newConnectionData);
      setCurrentClientWordPressConnection(savedConnection);
      // Form fields are the source of truth for saving, no need to reset them from savedConnection here
      setApiStatus({ status: 'success', message: '✓ Connection saved successfully!' });
      setError(null);
    } catch (err) {
      console.error("Failed to save WordPress connection:", err);
      const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
      setError(`Failed to save WordPress connection: ${errorMessage}. See console for details.`);
      setApiStatus({ status: 'error', message: `✗ Failed to save connection: ${errorMessage}` });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteClientWordPressConnection = async () => {
    if (!selectedClientId || !currentClientWordPressConnection) {
      setError("No connection to delete for the selected client.");
      setApiStatus({ status: 'error', message: "No connection to delete." });
      return;
    }

    if (window.confirm('Are you sure you want to delete this connection? This cannot be undone.')) {
      setIsLoading(true);
      setApiStatus({ status: 'idle', message: 'Deleting connection...' });
      try {
        await databaseService.wordPressConnection.deleteByClientId(selectedClientId);
        setCurrentClientWordPressConnection(null);
        setWordPressUrl(looksLikeDomain(clientNameInput) ? `https://${clientNameInput.replace(/^https?:\/\//, '').replace(/\/+$/, '')}` : '');
        setWordPressUsername('');
        setWordPressPassword('');
        setApiStatus({ status: 'success', message: '✓ Connection deleted successfully.' });
        setError(null);
      } catch (error) {
        console.error("Failed to delete WordPress connection:", error);
        setError("Failed to delete WordPress connection. See console for details.");
        setApiStatus({ status: 'error', message: '✗ Failed to delete connection.' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const testWordPressApiConnection = async () => {
    if (!wordPressUrl.trim()) {
      setApiStatus({ status: 'error', message: 'Please enter a WordPress site URL' });
      return;
    }
  
    if (!wordPressUsername || !wordPressPassword) {
      setApiStatus({ status: 'error', message: 'Please enter both username and password for authentication' });
      return;
    }
  
    setIsTestingApi(true);
    setApiStatus({ status: 'idle', message: 'Testing connection...' });
  
    try {
      const endpointUrl = `${wordPressUrl.replace(/\/$/, '')}/wp-json/wp-plugin-analyzer/v1/plugins`;
      console.log(`Testing endpoint: ${endpointUrl}`);

      const response = await axios.post(endpointUrl, 
        { test_connection: true }, 
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Basic ${btoa(`${wordPressUsername}:${wordPressPassword}`)}`
          },
          validateStatus: (status) => {
            return (status >= 200 && status < 300) || status === 404;
          }
        }
      );

      console.log(`Response status: ${response.status}`);

      if (response.status === 404) {
        const responseBody = response.data;
        if (responseBody && typeof responseBody === 'object' && responseBody.code === 'rest_no_route') {
            console.log('Connection successful (endpoint not found, but got a valid WP REST API response).');
            setApiStatus({ status: 'success', message: '✓ Connection successful.' });
            setCurrentClientWordPressConnection(prev => ({...(prev || {}), is_connected: true, last_tested: new Date().toISOString()} as WordPressConnection));
            return;
        } else {
            console.error('Received a 404, but not a standard WordPress REST API response.', responseBody);
            setApiStatus({ status: 'error', message: '✗ Connection failed: The URL returned a 404 error that does not seem to be from WordPress.' });
            setCurrentClientWordPressConnection(prev => ({...(prev || {}), is_connected: false, last_tested: new Date().toISOString()} as WordPressConnection));
            return;
        }
      }

      const data = response.data;
      console.log('Connection test successful, response data:', data);

      if (data.success) {
        setApiStatus({ status: 'success', message: '✓ Connection successful: The custom plugin endpoint responded correctly.' });
        setCurrentClientWordPressConnection(prev => ({...(prev || {}), is_connected: true, last_tested: new Date().toISOString()} as WordPressConnection));
      } else {
        setApiStatus({ status: 'error', message: `✗ Connection failed: ${data.message || 'The endpoint responded with an error.'}` });
        setCurrentClientWordPressConnection(prev => ({...(prev || {}), is_connected: false, last_tested: new Date().toISOString()} as WordPressConnection));
      }

    } catch (error) {
      console.error('An unexpected error occurred during the connection test:', error);
      let friendlyMessage = 'An unknown error occurred. This could be a network issue, a CORS problem, or the server might be down.';

      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorData = error.response.data;
          if (errorData && (errorData.code === 'rest_forbidden' || errorData.code === 'incorrect_password')) {
            friendlyMessage = 'Authentication failed. Please check your username and application password.';
          } else {
            friendlyMessage = `Connection failed with status ${error.response.status}: ${errorData.message || 'An error occurred.'}`;
          }
        } else if (error.request) {
          friendlyMessage = 'Network request failed. The server did not respond. This could be a CORS issue, a network error, or the WordPress site is offline.';
        } else {
          friendlyMessage = `An error occurred: ${error.message}`;
        }
      } 
      setApiStatus({ status: 'error', message: `✗ ${friendlyMessage}` });
      setCurrentClientWordPressConnection(prev => ({...(prev || {}), is_connected: false, last_tested: new Date().toISOString()} as WordPressConnection));
    } finally {
      setIsTestingApi(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900 text-slate-100 p-4 md:p-8 selection:bg-sky-500 selection:text-white">
      {!isAuthenticated ? (
        <div className="max-w-md mx-auto mt-20 bg-slate-800 shadow-2xl rounded-xl p-6 md:p-8">
          <h2 className="text-3xl font-bold text-sky-300 mb-6 text-center">Enter Access Key</h2>
          <input
            type="password" // Use password type to hide key
            value={userInputKey}
            onChange={(e) => setUserInputKey(e.target.value)}
            onKeyUp={(e) => e.key === 'Enter' && handleKeyCheck()}
            placeholder="Enter your key"
            className="w-full p-3 bg-slate-900 border-2 border-slate-700 rounded-lg text-slate-200 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-150 mb-4"
          />
          <button
            onClick={handleKeyCheck}
            className="w-full flex items-center justify-center px-8 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-bold rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-opacity-75 transition-all duration-150"
          >
            Submit Key
          </button>
        </div>
      ) : (
        <> {/* Authenticated content */}
          <header className="text-center mb-10">
            <h1 className="text-5xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-600">
              WordPress Plugin Analyzer
            </h1>
            <p className="mt-3 text-lg text-slate-400 max-w-2xl mx-auto">
              Track plugin changes for your websites. Data is stored in a central database.
            </p>
          </header>

          <div className="max-w-5xl mx-auto bg-slate-800 shadow-2xl rounded-xl p-6 md:p-8">
            <div className="mb-8 space-y-6">
              <div>
                <label htmlFor="clientNameInput" className="block text-xl font-semibold text-sky-300 mb-2">
                  Domain Name
                </label>
                <input
                  type="text"
                  id="clientNameInput"
                  value={clientNameInput}
                  onChange={(e) => handleClientNameChange(e.target.value)}
                  placeholder="Enter client name (e.g., example.com)"
                  className="w-full p-3 bg-slate-900 border-2 border-slate-700 rounded-lg text-slate-200 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-150"
                  disabled={isLoading}
                  aria-required="true"
                />
                {availableClients.length > 0 && (
                  <div className="mt-2 text-sm">
                    <div className="flex flex-wrap gap-2">
                      {availableClients.map(c => (
                        <div key={c.id} className="relative group">
                          <button
                            onClick={() => handleClientSelection(c.id)}
                            className={`px-3 py-1.5 pr-7 rounded-full text-sm font-medium transition-all duration-200 flex items-center ${
                              selectedClientId === c.id
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            {c.name}
                            <span 
                              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedClientId(c.id);
                                handleDeleteClient();
                              }}
                              title={`Delete ${c.name}`}
                            >
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                className="h-3 w-3" 
                                viewBox="0 0 20 20" 
                                fill="currentColor"
                              >
                                <path 
                                  fillRule="evenodd" 
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
                                  clipRule="evenodd" 
                                />
                              </svg>
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedClientId && (
                  <div className="mt-3">

                  </div>
                )}
              </div>
              {/* Tab Navigation */}
              <div className="border-b border-slate-700 mt-6">
                <div className="flex space-x-1">
                  <button
                    onClick={() => setActiveTab('wordpress')}
                    className={`py-2 px-4 font-medium text-sm rounded-t-lg transition-colors duration-150 ${
                      activeTab === 'wordpress'
                        ? 'bg-slate-700 text-sky-400 border-b-2 border-sky-400'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                    }`}
                  >
                    Fetch from WordPress
                  </button>
                  <button
                    onClick={() => setActiveTab('manual')}
                    className={`py-2 px-4 font-medium text-sm rounded-t-lg transition-colors duration-150 ${
                      activeTab === 'manual'
                        ? 'bg-slate-700 text-sky-400 border-b-2 border-sky-400'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                    }`}
                  >
                    Manual Input
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div className="pt-2">
                {activeTab === 'manual' ? (
                  <div>
                    <label htmlFor="pluginText" className="block text-xl font-semibold text-sky-300 mb-2">
                      Paste Plugin List Text
                    </label>
                    <textarea
                      id="pluginText"
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      placeholder="Paste the entire text content from your WordPress admin plugins page here..."
                      rows={10}
                      className="w-full p-4 bg-slate-900 border-2 border-slate-700 rounded-lg text-slate-200 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-150 resize-y"
                      disabled={isLoading}
                      aria-required={activeTab === 'manual'}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                    <label htmlFor="wordPressUrl" className="block text-sm font-medium text-slate-300 mb-1">
                      WordPress Site URL
                      <a 
                        href={wordPressUrl ? `${wordPressUrl.replace(/\/+$/, '')}/wp-admin/` : '#'} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ml-2 inline-flex items-center text-slate-400 hover:text-sky-400 transition-colors"
                        title="Open WordPress Admin in new tab"
                        onClick={(e) => {
                          if (!wordPressUrl) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className="h-4 w-4" 
                          viewBox="0 0 20 20" 
                          fill="currentColor"
                        >
                          <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                          <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                        </svg>
                      </a>
                    </label>
                      <input
                        type="url"
                        id="wordPressUrl"
                        value={wordPressUrl}
                        onChange={(e) => setWordPressUrl(e.target.value)}
                        placeholder="https://example.com"
                        className="w-full p-3 bg-slate-900 border-2 border-slate-700 rounded-lg text-slate-200 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-150"
                        disabled={isLoading}
                        aria-required={activeTab === 'wordpress'}
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        e.g., https://example.com
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="wordPressUsername" className="block text-sm font-medium text-slate-300 mb-1">
                          Username
                        </label>
                        <input
                          type="text"
                          id="wordPressUsername"
                          value={wordPressUsername}
                          onChange={(e) => setWordPressUsername(e.target.value)}
                          placeholder="WordPress username"
                          className="w-full p-3 bg-slate-900 border-2 border-slate-700 rounded-lg text-slate-200 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-150"
                          disabled={isLoading}
                          aria-required={activeTab === 'wordpress'}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <label htmlFor="wordPressPassword" className="block text-sm font-medium text-slate-300">
                            Application Password
                          </label>
                          <a 
                            href="https://make.wordpress.org/core/2020/11/05/application-passwords-integration-guide/" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-slate-400 hover:text-sky-400 transition-colors duration-150"
                            title="Learn more about Application Passwords"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </a>
                        </div>
                        <div className="flex flex-col"> {/* Outer container for password input row and checkbox row */}
                          <div className="flex gap-2"> {/* Password input and Test Connection button */}
                            <input
                              type="password"
                            id="wordPressPassword"
                            value={wordPressPassword}
                            onChange={(e) => setWordPressPassword(e.target.value)}
                            placeholder="Application Password"
                            className="flex-1 p-3 bg-slate-900 border-2 border-slate-700 rounded-lg text-slate-200 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-150"
                            disabled={isLoading}
                            aria-required={activeTab === 'wordpress'}
                          />
                          <button
                            onClick={testWordPressApiConnection}
                            disabled={isLoading || isTestingApi || !wordPressUrl.trim() || !wordPressUsername.trim() || !wordPressPassword.trim()}
                            className="px-4 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition-colors duration-150 flex items-center justify-center whitespace-nowrap"
                            type="button"
                            title="Test WordPress REST API connection"
                          >
                            {isTestingApi ? 'Testing...' : 'Test Connection'}
                          </button>
                        </div>
                      </div> {/* This closes the flex-col div started above */}
                      </div>
                    </div>
                    
                    {apiStatus.message && (
                      <div className={`p-3 rounded-lg ${
                        apiStatus.status === 'success' ? 'bg-green-900/30 border border-green-800' : 
                        apiStatus.status === 'error' ? 'bg-red-900/30 border border-red-800' : 'bg-slate-800/50'
                      }`}>
                        <p className={`text-sm ${
                          apiStatus.status === 'success' ? 'text-green-400' : 
                          apiStatus.status === 'error' ? 'text-red-400' : 'text-slate-400'
                        }`}>
                          {apiStatus.message}
                        </p>
                      </div>
                    )}
                    
                    <div className="flex space-x-2 mt-4">
                      <button
                        onClick={saveClientWordPressConnection}
                        disabled={isLoading || !clientNameInput.trim() || !wordPressUrl.trim()}
                        className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg transition-colors duration-150 flex items-center justify-center whitespace-nowrap disabled:opacity-50"
                        type="button"
                        title={!clientNameInput.trim() ? "Enter a client name to save connection" : "Save current WordPress connection for this client"}
                      >
                        {currentClientWordPressConnection ? 'Update Connection' : 'Save Connection'}
                      </button>
                      {currentClientWordPressConnection && (
                        <button
                          onClick={deleteClientWordPressConnection}
                          disabled={isLoading}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-150 flex items-center justify-center whitespace-nowrap disabled:opacity-50"
                          type="button"
                          title="Delete WordPress connection for this client"
                        >
                          Delete Connection
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={handleAnalyze}
                disabled={
                  isLoading || 
                  !clientNameInput.trim() || 
                  (activeTab === 'manual' ? !inputText.trim() : !wordPressUrl.trim())
                }
                className="w-full flex items-center justify-center px-8 py-4 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-bold rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-opacity-75 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={
                  activeTab === 'manual' 
                    ? 'Analyze pasted plugin list for the specified client'
                    : 'Fetch and analyze plugins from WordPress site'
                }
              >
                {isLoading && (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isLoading ? 'Analyzing...' : activeTab === 'manual' ? 'Analyze Plugins' : 'Fetch & Analyze Plugins'}
              </button>
            </div>

            {error && (
              <div className="my-6 p-4 bg-red-800 border border-red-600 text-red-100 rounded-lg shadow-md" role="alert">
                <strong className="font-semibold">Error:</strong> {error}
              </div>
            )}
            
            {!error && (
              <>
                {comparison && currentAnalysis && (
                  <div className="my-8 p-6 bg-slate-700 rounded-lg shadow-xl">
                    <h2 className="text-3xl font-bold text-sky-300 mb-2">
                      Comparison for <span className="text-sky-400">{currentAnalysis.clientName}</span>
                    </h2>
                    <p className="text-sm text-slate-400 mb-6">
                      Current: {getFormattedDate(currentAnalysis.timestamp)} vs. Previous: {
                        snapshots.filter(s => s.id !== currentAnalysis?.timestamp && new Date(s.timestamp).getTime() < new Date(currentAnalysis.timestamp).getTime()).length > 0
                        ? getFormattedDate(snapshots.filter(s => s.id !== currentAnalysis?.timestamp && new Date(s.timestamp).getTime() < new Date(currentAnalysis.timestamp).getTime()).sort((a,b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0].timestamp)
                        : 'N/A (First analysis for this client or no prior snapshot found)'
                      }
                    </p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <PluginListCard
                        title="Added Plugins"
                        plugins={comparison.added}
                        icon="➕"
                        bgColorClass="bg-emerald-800"
                        textColorClass="text-emerald-100"
                        emptyMessage="No new plugins added."
                      />
                      <PluginListCard
                        title="Removed Plugins"
                        plugins={comparison.removed}
                        icon="➖"
                        bgColorClass="bg-rose-800"
                        textColorClass="text-rose-100"
                        emptyMessage="No plugins removed."
                      />
                       <PluginListCard
                        title="Unchanged Plugins"
                        plugins={comparison.common}
                        icon="🔄"
                        bgColorClass="bg-sky-800"
                        textColorClass="text-sky-100"
                        emptyMessage="No common plugins found."
                      />
                    </div>
                  </div>
                )}

                {currentAnalysis && !comparison && (
                   <div className="my-8">
                    <PluginListCard
                      title={`Current List for ${currentAnalysis.clientName} (${getFormattedDate(currentAnalysis.timestamp)})`}
                      plugins={currentAnalysis.plugins}
                      icon="📋"
                      bgColorClass="bg-slate-700"
                      emptyMessage={"No plugins found in this list."}
                    />
                  </div>
                )}

                {!isLoading && !error && !currentAnalysis && !selectedClientId && (
                     <div className="my-6 p-6 bg-slate-700 rounded-lg text-center">
                        <p className="text-xl text-slate-300">
                            {activeTab === 'manual' 
                              ? 'Enter a client name and paste a plugin list to get started, or select an existing client.'
                              : 'Enter a client name and WordPress site URL to fetch and analyze plugins.'}
                        </p>
                        <p className="text-sm text-slate-400 mt-2">
                            The app will {activeTab === 'manual' ? 'extract plugin names' : 'fetch plugins'}, save a snapshot to the database, and allow you to compare lists over time for each client.
                        </p>
                     </div>
                )}
                 {!isLoading && !error && !currentAnalysis && selectedClientId && snapshots.length === 0 && (
                     <div className="my-6 p-6 bg-slate-700 rounded-lg text-center">
                        <p className="text-xl text-slate-300">
                            No snapshots found for {clientNameInput}. Paste a plugin list to create the first one.
                        </p>
                     </div>
                )}
              </>
            )}
             {snapshots.length > 0 && (
                <div className="mt-12 pt-8 border-t border-slate-700">
                  <h2 className="text-3xl font-bold text-sky-300 mb-6 text-center">
                    Snapshot History for {selectedClientId ? availableClients.find(c=>c.id === selectedClientId)?.name : clientNameInput}
                  </h2>
                  <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
                    {snapshots.map((snapshot) => (
                      <details key={snapshot.id} className="bg-slate-700 p-4 rounded-lg shadow-lg open:ring-2 open:ring-sky-500 transition-all group">
                      <summary className="font-semibold text-lg text-sky-400 cursor-pointer hover:text-sky-300 flex justify-between items-center">
                        <span>
                          {clientNameInput || availableClients.find(c=>c.id === snapshot.client_id)?.name} - {getFormattedDate(snapshot.timestamp)} ({snapshot.plugins.length} plugins)
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSnapshot(snapshot.id, snapshot.timestamp);
                          }}
                          disabled={deletingSnapshotId === snapshot.id || isLoading}
                          className="px-3 py-1 text-xs bg-red-700 hover:bg-red-800 text-white rounded-md shadow-sm transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed ml-4"
                        >
                          {deletingSnapshotId === snapshot.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </summary>
                      <ul className="mt-3 space-y-1 pl-4 list-disc list-inside text-sm text-slate-300">
                        {snapshot.plugins.map((plugin, index) => (
                          <li key={index}>{plugin}</li>
                        ))}
                      </ul>
                      {snapshot.raw_input && (
                        <details className="mt-3 text-xs">
                          <summary className="cursor-pointer text-slate-400 hover:text-slate-200">Show Raw Input</summary>
                          <pre className="mt-1 p-2 bg-slate-800 rounded text-slate-400 max-h-40 overflow-auto whitespace-pre-wrap break-all">
                            {snapshot.raw_input}
                          </pre>
                        </details>
                      )}
                    </details>
                    ))}
                  </div>
                </div>
              )}
          </div>

          <footer className="text-center mt-12 py-6 border-t border-slate-700">
            <p className="text-sm text-slate-500">
              WP Plugin Analyzer &copy; {new Date().getFullYear()}. Powered by React, Neon DB, and Gemini API.
            </p>
            <p className="text-xs text-slate-600 mt-1">
              Plugin snapshot data is stored in a central database.
            </p>
          </footer>
        </>
      )}
    </div>
  );
};

export default App;